import {
  Button,
  CloseButton,
  Icon,
  Text,
  useDisclosure,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Paperclip, Search, Trash2 } from 'lucide-react'
import { Reorder, useMotionValue } from 'motion/react'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DropzoneVariant,
  useDropzoneStyles,
} from '~/components/shared/componentStyles/Dropzone.style'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import {
  DialogTitle,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '~/components/ui/dialog'
import { formatFileSize } from '~/utils/common'

import { Context } from './Context'
import { FileWithHashKey } from './types'

const ATTACHMENT_ROW_SELECTOR = '[data-attachment-row]'

interface FilePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  fileUrl: string | null
  fileName: string
}

const acceptedFileStyle = {
  alignItems: 'center',
  background: 'var(--chakra-colors-bg-panel)',
  borderRadius: '5px',
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
  marginBottom: '4px',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px',
  width: '100%',
}

const FilePreviewModal = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
}: FilePreviewModalProps) => {
  const { t } = useTranslation()
  const isImage = fileName.match(/\.(jpeg|jpg|gif|png|webp|bmp)$/i) !== null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          <DialogTitle>{fileName}</DialogTitle>
        </ModalHeader>
        <CloseButton
          size="sm"
          position="absolute"
          top="2"
          insetEnd="2"
          aria-label={t('global.close', 'Close')}
          onClick={onClose}
        />
        {/* ph-no-capture: outgoing fax attachments are PHI; PostHog replay
            records this area as an empty placeholder */}
        <ModalBody
          className="ph-no-capture"
          p={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          maxH="80vh"
          overflow="auto"
        >
          {fileUrl && isImage ? (
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                margin: 'auto',
              }}
              draggable="false"
            />
          ) : (
            fileUrl && (
              <iframe
                src={fileUrl}
                style={{
                  width: '100%',
                  height: '80vh',
                  border: 'none',
                }}
                title={fileName}
                draggable="false"
              />
            )
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

/**
 * Focuses the row that took the removed row's place, else the row before it,
 * else the dropzone, once the list has re-rendered without the removed row.
 */
const useFocusAfterRemoval = () => {
  const {
    dropzoneState: { rootRef },
  } = useContext(Context)

  return (removedRow: Element | null) => {
    const root = rootRef.current
    if (!root) return
    const rowsBefore = Array.from(
      root.querySelectorAll(ATTACHMENT_ROW_SELECTOR),
    )
    const removedIndex = removedRow ? rowsBefore.indexOf(removedRow) : -1

    setTimeout(() => {
      const rows = root.querySelectorAll<HTMLElement>(ATTACHMENT_ROW_SELECTOR)
      const targetRow =
        removedIndex >= 0
          ? (rows[removedIndex] ?? rows[removedIndex - 1])
          : undefined
      const target =
        targetRow?.querySelector<HTMLElement>(
          "button:not([disabled]):not([aria-disabled='true'])",
        ) ?? root
      target.focus()
    })
  }
}

const useFilePreview = (file: File) => {
  const { open: isOpen, onOpen, onClose } = useDisclosure()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const openPreview = () => {
    setPreviewUrl(URL.createObjectURL(file))
    onOpen()
  }

  const closePreview = () => {
    onClose()
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  return { isOpen, previewUrl, openPreview, closePreview }
}

interface FileActionButtonProps {
  label: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
  'aria-disabled'?: boolean
}

const FileActionButton = ({
  label,
  onClick,
  children,
  ...rest
}: FileActionButtonProps) => (
  // The name holds the file name (PHI). Visually hidden text, unlike
  // aria-label, is masked by PostHog replay (maskTextSelector: '*' in
  // utils/initializePosthog.ts) and not serialized by Sentry.
  <Button size="sm" variant="ghost" onClick={onClick} {...rest}>
    {children}
    <VisuallyHidden>{label}</VisuallyHidden>
  </Button>
)

export default function ContentAcceptedMulti() {
  const { acceptedFiles, coverPage, setFiles } = useContext(Context)
  const handleOnReorder = (items: FileWithHashKey[]) => {
    setFiles({ accepted: items })
  }
  return (
    <VStack
      justify="center"
      paddingBottom="12px"
      paddingTop="12px"
      gap={2}
      width="100%"
      height="fit-content"
    >
      <Reorder.Group
        axis="y"
        onReorder={handleOnReorder}
        values={acceptedFiles}
        style={{ width: '100%', paddingLeft: 0 }}
      >
        {coverPage && <CoverPage file={coverPage} />}
        {acceptedFiles.map((file) => (
          <DraggableItem file={file} key={`${file.name}-${file.hashKey}`} />
        ))}
      </Reorder.Group>
    </VStack>
  )
}

export function CoverPage({ file }: { file: File }) {
  const { t } = useTranslation()
  const announce = useAnnounce()
  const { handleCoverPageDelete } = useContext(Context)
  const focusAfterRemoval = useFocusAfterRemoval()
  const styles = useDropzoneStyles({
    variant: DropzoneVariant.FileAccepted,
  })
  const { isOpen, previewUrl, openPreview, closePreview } = useFilePreview(file)

  const removeItem = (event: React.MouseEvent<HTMLButtonElement>) => {
    focusAfterRemoval(event.currentTarget.closest(ATTACHMENT_ROW_SELECTOR))
    handleCoverPageDelete?.()
    announce(
      t('contentAcceptedMulti.fileRemoved', '{{fileName}} removed', {
        fileName: file.name,
      }),
    )
  }

  return (
    <>
      <li style={acceptedFileStyle} data-attachment-row>
        <Icon css={styles.icon} aria-hidden>
          <Paperclip size="100%" />
        </Icon>
        <Text
          css={{
            ...styles.text,
            flex: 1,
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          {file.name} ({formatFileSize(file.size)})
        </Text>
        <FileActionButton
          label={t('contentAcceptedMulti.previewFile', 'Preview {{fileName}}', {
            fileName: file.name,
          })}
          onClick={openPreview}
        >
          <Icon as={Search} aria-hidden />
        </FileActionButton>
        <FileActionButton
          label={t('contentAcceptedMulti.removeFile', 'Remove {{fileName}}', {
            fileName: file.name,
          })}
          onClick={removeItem}
        >
          <Icon aria-hidden>
            <Trash2 size={16} />
          </Icon>
        </FileActionButton>
      </li>

      <FilePreviewModal
        isOpen={isOpen}
        onClose={closePreview}
        fileUrl={previewUrl}
        fileName={file.name}
      />
    </>
  )
}

function DraggableItem({ file }: { file: FileWithHashKey }) {
  const { t } = useTranslation()
  const announce = useAnnounce()
  const {
    acceptedFiles,
    potentialDuplicatePaths,
    props: { maxFileSize },
    setCurrentVariant,
    setFiles,
  } = useContext(Context)
  const focusAfterRemoval = useFocusAfterRemoval()
  const isCoverPage = file.name === 'Cover Page.pdf'
  const [isDragging, setIsDragging] = useState(false)
  const y = useMotionValue(0)
  const { isOpen, previewUrl, openPreview, closePreview } = useFilePreview(file)

  const styles = useDropzoneStyles({
    variant: DropzoneVariant.FileAccepted,
  })

  const isPotentialDuplicate = Boolean(
    file.path && potentialDuplicatePaths.includes(file.path),
  )
  const isTooLarge = file.size > maxFileSize
  // Find the file by reference (exact same object) rather than by name
  const fileIndex = acceptedFiles.findIndex((f) => f === file)
  const canReorder = acceptedFiles.length > 1 && !isCoverPage
  const isFirst = fileIndex <= 0
  const isLast = fileIndex === acceptedFiles.length - 1

  function removeItem(event: React.MouseEvent<HTMLButtonElement>) {
    const filesNext = [...acceptedFiles]
    if (fileIndex !== -1) {
      filesNext.splice(fileIndex, 1)
    }

    focusAfterRemoval(event.currentTarget.closest(ATTACHMENT_ROW_SELECTOR))
    setFiles({ accepted: filesNext })
    if (!filesNext.length) {
      setCurrentVariant(DropzoneVariant.Default)
    }
    announce(
      t('contentAcceptedMulti.fileRemoved', '{{fileName}} removed', {
        fileName: file.name,
      }),
    )
  }

  const moveItem =
    (offset: -1 | 1) => (event: React.MouseEvent<HTMLButtonElement>) => {
      const targetIndex = fileIndex + offset
      if (
        fileIndex === -1 ||
        targetIndex < 0 ||
        targetIndex >= acceptedFiles.length
      ) {
        return
      }

      // React re-inserts the moved row's DOM node, which blurs the pressed button.
      const button = event.currentTarget
      const filesNext = [...acceptedFiles]
      filesNext.splice(fileIndex, 1)
      filesNext.splice(targetIndex, 0, file)
      setFiles({ accepted: filesNext })
      setTimeout(() => button.focus())
      announce(
        t(
          'contentAcceptedMulti.fileMoved',
          '{{fileName}} moved to position {{position}} of {{total}}',
          {
            fileName: file.name,
            position: targetIndex + 1,
            total: filesNext.length,
          },
        ),
      )
    }

  const handleDragEnd = () => {
    setIsDragging(false)
  }
  const handleDragStart = () => {
    setIsDragging(true)
  }

  const fileLabel = `${file.name} (${formatFileSize(file.size)})`
  const status =
    isPotentialDuplicate && isTooLarge
      ? t(
          'contentAcceptedMulti.possibleDuplicateAndTooLarge',
          'Possible duplicate, too large',
        )
      : isPotentialDuplicate
        ? t('contentAcceptedMulti.possibleDuplicate', 'Possible duplicate')
        : isTooLarge
          ? t('contentAcceptedMulti.tooLarge', 'Too large')
          : null

  return (
    <>
      <Reorder.Item
        draggable={!isCoverPage}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        value={file}
        id={file.hashKey.toString() || file.name}
        data-attachment-row
        style={{
          ...acceptedFileStyle,
          y,
          cursor: isCoverPage ? 'default' : 'grab',
          boxShadow: isDragging
            ? '5px 5px 10px rgba(0,0,0,0.3)'
            : '0px 1px 3px rgba(0, 0, 0, 0.2)',
        }}
      >
        <Icon css={styles.icon} aria-hidden>
          <Paperclip size="100%" />
        </Icon>
        <Text
          css={{
            ...styles.text,
            ...(isPotentialDuplicate && { color: 'fg.warning' }),
            ...(isTooLarge && { color: 'fg.error' }),
            flex: 1,
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          {status
            ? t(
                'contentAcceptedMulti.fileWithStatus',
                '{{file}} – {{status}}',
                {
                  file: fileLabel,
                  status,
                },
              )
            : fileLabel}
        </Text>
        {canReorder && (
          <>
            <FileActionButton
              label={t('contentAcceptedMulti.moveUp', 'Move {{fileName}} up', {
                fileName: file.name,
              })}
              aria-disabled={isFirst || undefined}
              onClick={moveItem(-1)}
            >
              <Icon as={ChevronUp} aria-hidden />
            </FileActionButton>
            <FileActionButton
              label={t(
                'contentAcceptedMulti.moveDown',
                'Move {{fileName}} down',
                { fileName: file.name },
              )}
              aria-disabled={isLast || undefined}
              onClick={moveItem(1)}
            >
              <Icon as={ChevronDown} aria-hidden />
            </FileActionButton>
          </>
        )}
        <FileActionButton
          label={t('contentAcceptedMulti.previewFile', 'Preview {{fileName}}', {
            fileName: file.name,
          })}
          onClick={openPreview}
        >
          <Icon as={Search} aria-hidden />
        </FileActionButton>
        <FileActionButton
          label={t('contentAcceptedMulti.removeFile', 'Remove {{fileName}}', {
            fileName: file.name,
          })}
          onClick={removeItem}
        >
          <Icon aria-hidden>
            <Trash2 size={16} />
          </Icon>
        </FileActionButton>
      </Reorder.Item>

      <FilePreviewModal
        isOpen={isOpen}
        onClose={closePreview}
        fileUrl={previewUrl}
        fileName={file.name}
      />
    </>
  )
}
